import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BankAccount, CreateBankAccount, UpdateBankAccount } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['bank-accounts', companyId] as const;

export function useBankAccounts(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () =>
      api<BankAccount[]>(`/companies/${companyId}/bank-accounts?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useBankAccount(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['bank-accounts', companyId, id],
    queryFn: () => api<BankAccount>(`/companies/${companyId}/bank-accounts/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateBankAccount(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateBankAccount, 'companyId'>) =>
      apiJson<typeof input, BankAccount>('POST', `/companies/${companyId}/bank-accounts`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateBankAccount(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBankAccount }) =>
      apiJson<UpdateBankAccount, BankAccount>(
        'PATCH',
        `/companies/${companyId}/bank-accounts/${id}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteBankAccount(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, BankAccount>('DELETE', `/companies/${companyId}/bank-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
